import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { localizeValue } from "../lib/displayValues";
import Seo from "../components/Seo";
import LocationPicker from "../components/LocationPicker";
import { findCity } from "../data/locations";
import useGeoLocation from "../lib/useGeoLocation";
import {
  Store, MapPin, Phone, ExternalLink, ArrowRight, Loader2, Info, ShieldCheck,
} from "lucide-react";

/**
 * ShowroomsPage — "أشهر معارض السيارات حسب الموقع" directory.
 * The user picks a country/region/city from a single smart LocationPicker
 * (with fast type-ahead search across 12 countries + 130+ cities) and
 * sees the famous dealerships there.  Like the rest of the directory
 * pages, CARTAL is purely a broker: each showroom card has a "Visit
 * website" CTA that links out to the dealership.
 */
/**
 * (Removed) CITY_HERO landmark map — replaced by a smart cascading
 * LocationPicker so users find their city via search instead of
 * scrolling through landmark photos.
 */

export default function ShowroomsPage() {
  const { lang } = useI18n();
  const [cities, setCities] = useState([]);   // cities that actually have showrooms (for count chips)
  const [city, setCity] = useState("Riyadh");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { detectedCityId } = useGeoLocation();
  const [autoApplied, setAutoApplied] = useState(false);

  // First-load geo auto-detect: if we successfully detect a city AND the user
  // hasn't manually changed it yet, switch to the detected one.
  useEffect(() => {
    if (autoApplied || !detectedCityId) return;
    setCity(detectedCityId);
    setAutoApplied(true);
  }, [detectedCityId, autoApplied]);

  useEffect(() => {
    apiClient
      .get("/providers/showrooms/cities")
      .then(({ data }) => setCities(data.items || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    // iter335 — send Arabic/English city labels along with the id so
    // the backend can match against `profile.city` regardless of the
    // language the dealer registered with.
    const meta = findCity(city);
    apiClient
      .get("/providers/showrooms", {
        params: {
          city,
          city_ar: meta?.ar || "",
          city_en: meta?.en || "",
        },
      })
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, [city]);

  return (
    <div
      className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12"
      data-testid="showrooms-page"
    >
      <Seo
        title={lang === "ar" ? "دليل معارض السيارات في السعودية | كارتال" : "Car Showrooms Directory in Saudi Arabia | CARTAL"}
        description={lang === "ar" ? "تصفّح معارض السيارات الموثّقة في مدن السعودية — بيانات اتصال، مواقع، إعلانات حالية، على منصة كارتال." : "Browse verified car showrooms across Saudi cities — contact details, locations and live listings on CARTAL."}
        canonicalPath="/showrooms"
      />
      <div className="flex items-start gap-4 max-w-3xl mb-2">
        <span
          className="inline-flex items-center justify-center w-12 h-12 rounded-2xl shrink-0"
          style={{
            backgroundColor: "rgba(201,165,92,0.10)",
            border: "1px solid rgba(201,165,92,0.45)",
          }}
        >
          <Store className="w-6 h-6 text-[#8a6f2e]" />
        </span>
        <div>
          <div className="overline overline-gold mb-1">
            {lang === "ar" ? "دليل كارتال" : "CARTAL Directory"}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-medium text-[#0a0a0a] leading-tight">
            {lang === "ar" ? "أشهر معارض السيارات حسب الموقع" : "Famous Car Showrooms by Location"}
          </h1>
        </div>
      </div>
      <p className="text-[#525252] max-w-3xl text-base leading-relaxed mb-6">
        {lang === "ar"
          ? "اختر موقعك بدقة من المملكة العربية السعودية، دول الخليج، أو الدول المجاورة. الأسعار والتواصل تتم مباشرة مع المعرض."
          : "Pick your location precisely — Saudi Arabia, the GCC, or neighbouring countries. Pricing and contact happen directly with the showroom."}
      </p>

      {/* Smart cascading location picker (replaces the old city tabs + landmark hero) */}
      <div className="max-w-2xl mb-3" data-testid="showrooms-location-section">
        <label className="overline mb-2 block">
          {lang === "ar" ? "البحث حسب الموقع" : "SEARCH BY LOCATION"}
        </label>
        <LocationPicker
          value={city}
          onChange={(id) => id && setCity(id)}
          testId="showrooms-location"
        />
        {/* Quick-jump chips for cities that actually have showrooms in our directory */}
        {cities.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="showrooms-cities-with-data">
            <span className="text-[10px] uppercase tracking-wider text-[#8a8a8a] me-1 self-center">
              {lang === "ar" ? "متوفر معارض في:" : "Showrooms available in:"}
            </span>
            {cities.map((c) => {
              const meta = findCity(c.id);
              const label = meta ? (lang === "ar" ? meta.ar : meta.en) : localizeValue(c.id, lang);
              const active = city === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCity(c.id)}
                  className={`inline-flex items-center gap-1 rounded-full text-xs font-semibold px-2.5 py-1 border transition-colors ${
                    active
                      ? "bg-[#0a0a0a] text-[#c9a55c] border-[#0a0a0a]"
                      : "bg-white text-[#3a3a3a] border-[#0a0a0a]/10 hover:border-[#c9a55c]"
                  }`}
                  data-testid={`showrooms-quick-${c.id}`}
                >
                  {label}
                  <span className="text-[10px] opacity-70">({c.count})</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="h-5" />

      {/* Broker disclaimer */}
      <div
        className="flex items-start gap-3 rounded-2xl p-4 mb-8"
        style={{ backgroundColor: "#fafaf7", border: "1px solid rgba(201,165,92,0.30)" }}
      >
        <Info className="w-4 h-4 text-[#8a6f2e] mt-0.5 shrink-0" />
        <p className="text-xs text-[#525252] leading-relaxed">
          {lang === "ar"
            ? "كارتال تعرض المعارض كمرجع فقط. الزيارة والاتفاق والشراء تتم مباشرة مع المعرض، وكارتال ليست طرفاً في أي تعامل."
            : "CARTAL lists showrooms for reference only. Visiting, negotiating, and purchasing happen directly with the dealership. CARTAL is not a party to any transaction."}
        </p>
      </div>

      {/* Showroom grid */}
      {loading ? (
        <div className="inline-flex items-center gap-2 text-[#6b6b6b]">
          <Loader2 className="w-4 h-4 animate-spin" />
          {lang === "ar" ? "جاري التحميل..." : "Loading..."}
        </div>
      ) : items.length === 0 ? (
        <div className="surface rounded-2xl p-10 text-center text-[#6b6b6b]">
          {lang === "ar" ? "لا توجد معارض مسجّلة لهذه المدينة بعد." : "No showrooms registered for this city yet."}
        </div>
      ) : (
        <div
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
          data-testid="showroom-grid"
        >
          {items.map((s) => (
            <ShowroomCard key={s.id} s={s} lang={lang} />
          ))}
        </div>
      )}

      <div className="mt-14 text-center">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-[#8a6f2e] hover:text-[#5a4615] font-semibold"
        >
          {lang === "ar" ? "العودة للصفحة الرئيسية" : "Back to homepage"}
          <ArrowRight className="w-4 h-4 rtl:rotate-180" />
        </Link>
      </div>
    </div>
  );
}

function ShowroomCard({ s, lang }) {
  const name = lang === "ar" ? s.name_ar : s.name_en;
  const address = lang === "ar" ? s.address_ar : s.address_en;
  const brands = lang === "ar" ? s.brands_ar : s.brands_en;
  const tags = lang === "ar" ? s.tags_ar : s.tags_en;
  const phoneE164 = s.phone ? `+966${String(s.phone).replace(/^0+/, "")}` : "";
  // A showroom is treated as an *authorised brand dealer* if the dataset
  // explicitly tags it as such (e.g. "وكيل رسمي" / "Authorised"). This is a
  // separate signal from CARTAL's user-account KYC verification — it means
  // the brand itself sanctions this dealership.
  const authorisedTags = (s.tags_ar || []).concat(s.tags_en || []).map(t => t.toLowerCase());
  const isAuthorised = authorisedTags.some(t => t.includes("وكيل") || t.includes("authoris") || t.includes("official"));
  return (
    <article
      className="rounded-2xl border-2 border-[#0a0a0a]/8 bg-white overflow-hidden hover:border-[#c9a55c] hover:shadow-md transition-all flex flex-col"
      data-testid={`showroom-card-${s.id}`}
    >
      <div className="p-5 flex items-start gap-4 border-b border-[#0a0a0a]/5">
        <span
          className="inline-flex items-center justify-center w-14 h-14 rounded-2xl shrink-0 font-display text-2xl font-bold"
          style={{
            backgroundColor: `${s.color}12`,
            border: `1.5px solid ${s.color}40`,
            color: s.color,
          }}
        >
          {s.logo_initials}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-base sm:text-lg font-semibold text-[#0a0a0a] leading-tight inline-flex items-center gap-2 flex-wrap">
            {name}
            {isAuthorised && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5"
                style={{ backgroundColor: "rgba(201,165,92,0.15)", color: "#8a6f2e", border: "1px solid rgba(201,165,92,0.45)" }}
                title={lang === "ar" ? "وكيل معتمد من الشركة المصنّعة" : "Authorised brand dealer"}
                data-testid={`showroom-authorised-${s.id}`}
              >
                <ShieldCheck className="w-3 h-3" />
                {lang === "ar" ? "وكيل معتمد" : "Authorised"}
              </span>
            )}
          </h3>
          {brands?.length > 0 && (
            <div className="text-xs text-[#6b6b6b] mt-1 truncate">
              {brands.join(" · ")}
            </div>
          )}
        </div>
      </div>

      <div className="p-5 flex-1 flex flex-col">
        <div className="text-xs text-[#525252] flex items-start gap-1.5">
          <MapPin className="w-3.5 h-3.5 mt-0.5 text-[#8a6f2e] shrink-0" />
          <span className="leading-relaxed">{address}</span>
        </div>

        {tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {tags.map((tag) => (
              <span
                key={tag}
                className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#0a0a0a]/15 text-[#525252]"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="mt-auto pt-5 flex flex-wrap items-center gap-2">
          <a
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-colors"
            style={{ backgroundColor: s.color, color: "#ffffff" }}
            data-testid={`showroom-visit-${s.id}`}
          >
            {lang === "ar" ? "زيارة الموقع" : "Visit website"}
            <ExternalLink className="w-3.5 h-3.5" style={{ color: "#ffffff" }} />
          </a>
          {phoneE164 && (
            <a
              href={`tel:${phoneE164}`}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold border border-[#0a0a0a]/15 text-[#0a0a0a] hover:border-[#c9a55c] transition-colors"
              data-testid={`showroom-phone-${s.id}`}
              dir="ltr"
            >
              <Phone className="w-3 h-3" /> {s.phone}
            </a>
          )}
        </div>
      </div>
    </article>
  );
}
